<template>
  <div v-on-click-outside="() => (showMenu = false)" class="relative">
    <button
      class="flex cursor-pointer items-center space-x-2 font-medium text-primary"
      data-cy="data-step-add-item"
      @click="showMenu = !showMenu"
    >
      <icon-base :icon="IconPlusSmall" />
      <span>{{ $t("COMMON.ADD_ITEM") }}</span>
    </button>
    <div
      v-if="showMenu"
      class="absolute bottom-full z-20 min-w-56 overflow-hidden rounded-md bg-white text-sm text-gray-500 shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
      :class="[flipElement ? 'right-0' : 'left-0']"
    >
      <div class="p-3">
        <search-input v-model="searchValue" no-margin no-padding />
        <div class="mt-4 max-h-56 space-y-4 overflow-y-scroll">
          <div
            v-for="(fieldGroup, key) in groupedFields"
            :key="key"
            class="space-y-2"
          >
            <div class="text-xs font-semibold text-gray-400 uppercase">
              {{ key }}
            </div>
            <div class="space-y-2 border-l border-gray-200 pr-4 pl-2">
              <template v-if="Array.isArray(fieldGroup)">
                <div v-for="field in fieldGroup" :key="field.id">
                  <button
                    v-if="isIndividualField(field)"
                    class="w-full cursor-pointer rounded-lg p-2 text-left transition-colors hover:bg-disabled"
                    :data-cy="`${field.name}--button`"
                    @click="handleSelectField(field)"
                  >
                    {{ field.label }}
                  </button>
                  <button
                    v-if="isTable(field)"
                    class="w-full cursor-pointer rounded-lg p-2 text-left transition-colors hover:bg-disabled"
                    :data-cy="`${field.title}--button`"
                    @click="handleSelectTable(field)"
                  >
                    {{ field.title }}
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import {
  fieldDefinitionToField,
  isIndividualField,
  isTable,
} from "@/helpers/checkout";
import { useCheckoutOptionsQuery } from "@/hooks/queries/options";
import { CheckoutApplicationFieldType } from "@/models/options";

import IconPlusSmall from "@/components/icons/IconPlusSmall.vue";
import IconBase from "@/components/ui/IconBase.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

import type { CheckoutDataField, CheckoutDataTable } from "@/models/checkout";

type GroupedFields = Record<string, (CheckoutDataField | CheckoutDataTable)[]>;

const CUSTOM_FIELD_ID_PLACEHOLDER = "custom";

const emit = defineEmits<{
  "field:select": [CheckoutDataField];
  "table:select": [CheckoutDataTable];
}>();

const { t } = useI18n();
const { data: checkoutOptions } = useCheckoutOptionsQuery();

const showMenu = ref(false);
const flipElement = ref(false);
const searchValue = ref("");

const groupedFields = computed(() => {
  const applicationFieldBlocks =
    checkoutOptions.value?.definition.application_blocks ?? [];

  const tableBlocks = checkoutOptions.value?.definition.table_blocks ?? [];

  const applicationFieldGroups = applicationFieldBlocks.reduce<GroupedFields>(
    (acc, current) => {
      acc[current.name] = current.fields.map((field) =>
        fieldDefinitionToField(current.id, field)
      );
      return acc;
    },
    {}
  );

  const tableGroup: CheckoutDataTable[] = tableBlocks?.map((table) => ({
    id: table.id,
    type: "table",
    layout: "table",
    fields: [],
    title: table.name,
    block: table.id,
  }));

  const customFieldGroup: GroupedFields = {
    custom: [
      {
        id: CUSTOM_FIELD_ID_PLACEHOLDER,
        type: "custom_field",
        name: CUSTOM_FIELD_ID_PLACEHOLDER,
        label: t("COMMON.CUSTOM_FIELD"),
        value_type: CheckoutApplicationFieldType.text,
      },
    ],
  };

  const groupedFields: GroupedFields = {
    tables: tableGroup,
    ...customFieldGroup,
    ...applicationFieldGroups,
  };

  if (searchValue.value) {
    return filterGroupFieldsBySearch(groupedFields);
  }

  return groupedFields;
});

const filterGroupFieldsBySearch = (groupedFields: GroupedFields) => {
  const filteredGroups: GroupedFields = {};

  for (const key in groupedFields) {
    filteredGroups[key] = groupedFields[key].filter((field) => {
      let fieldName = "";

      if (isTable(field)) {
        fieldName = field.title ?? "";
      }

      if (isIndividualField(field)) {
        fieldName = field.label ?? "";
      }

      return fieldName
        .toLocaleLowerCase()
        .includes(searchValue.value.trim().toLocaleLowerCase());
    });

    if (filteredGroups[key].length < 1) {
      delete filteredGroups[key];
    }
  }

  return filteredGroups;
};

const handleSelectField = (field: CheckoutDataField) => {
  const newField = structuredClone(field);

  if (newField.id === CUSTOM_FIELD_ID_PLACEHOLDER) {
    const id = crypto.randomUUID();
    newField.id = id;
    newField.name = `custom-field--${id}`;
  }

  emit("field:select", newField);
  showMenu.value = false;
};

const handleSelectTable = (table: CheckoutDataTable) => {
  const newTable = structuredClone(table);

  const id = crypto.randomUUID();
  newTable.id = id;

  emit("table:select", newTable);
  showMenu.value = false;
};
</script>
